import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, BookOpen, Lightbulb, BarChart3, GitCompareArrows, Send, Loader, Sparkles } from 'lucide-react';
import { submitPrompt, trackEvent } from '../../shared';
import templates from './templates';
import './PromptCrafter.css';

const iconMap = {
  FileText,
  BookOpen,
  Lightbulb,
  BarChart3,
  GitCompareArrows,
};

function PromptCrafter() {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);

  const handleTemplateClick = (key) => {
    const scaffold = t(`promptCrafter.templates.${key}.scaffold`);
    setPrompt(scaffold);
    setResponse('');
    setError('');
    setActiveTemplate(key);
    trackEvent('prompt_template_selected', { template: key });
  };

  const handleSubmit = async () => {
    if (!prompt.trim() || isLoading) return;

    setIsLoading(true);
    setError('');
    setResponse('');
    trackEvent('prompt_submitted', { promptLength: prompt.length });

    try {
      const result = await submitPrompt(prompt.trim());
      setResponse(result.response);
    } catch (err) {
      setError(t('promptCrafter.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  return (
    <div className="prompt-crafter">
      {/* Hero banner */}
      <section className="pc-hero">
        <div className="pc-hero-content">
          <div className="pc-hero-icon">
            <Sparkles size={28} />
          </div>
          <h1 className="pc-title">{t('promptCrafter.title')}</h1>
          <p className="pc-subtitle">{t('promptCrafter.subtitle')}</p>
        </div>
      </section>

      {/* Content area */}
      <div className="pc-content">
        <div className="pc-layout">
          {/* Main area */}
          <div className="pc-main">
            {/* Templates */}
            <div className="pc-templates">
              <p className="pc-templates-label">{t('promptCrafter.templatesLabel')}</p>
              <div className="pc-templates-grid">
                {templates.map(({ key, icon }) => {
                  const Icon = iconMap[icon];
                  return (
                    <button
                      key={key}
                      className={`pc-template-btn${activeTemplate === key ? ' pc-template-btn--active' : ''}`}
                      onClick={() => handleTemplateClick(key)}
                    >
                      <span className="pc-template-icon">
                        <Icon size={18} />
                      </span>
                      <span>{t(`promptCrafter.templates.${key}.label`)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Textarea + submit */}
            <div className="pc-input-area">
              <textarea
                className="pc-textarea"
                value={prompt}
                onChange={(e) => { setPrompt(e.target.value); setActiveTemplate(null); }}
                onKeyDown={handleKeyDown}
                placeholder={t('promptCrafter.placeholder')}
                rows={8}
              />
              <div className="pc-input-footer">
                <span className="pc-char-count">{prompt.length > 0 ? `${prompt.length} chars` : ''}</span>
                <button
                  className="pc-submit-btn"
                  onClick={handleSubmit}
                  disabled
                >
                  {isLoading ? <Loader size={18} className="pc-spinner" /> : <Send size={18} />}
                  <span>{isLoading ? t('promptCrafter.sending') : t('promptCrafter.submit')}</span>
                </button>
              </div>
            </div>

            {/* Error */}
            {error && <p className="pc-error">{error}</p>}

            {/* Response */}
            {response && (
              <div className="pc-response">
                <h3 className="pc-response-label">{t('promptCrafter.responseLabel')}</h3>
                <div className="pc-response-body">{response}</div>
              </div>
            )}
          </div>

          {/* Tips sidebar */}
          <aside className="pc-sidebar">
            <h3 className="pc-sidebar-title">{t('promptCrafter.tipsTitle')}</h3>
            <ul className="pc-tips-list">
              {[1, 2, 3, 4, 5].map((n) => (
                <li key={n} className="pc-tip">{t(`promptCrafter.tips.${n}`)}</li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default PromptCrafter;
